import { AxiosResponse, Method } from 'axios'
import ApiService, { IRequestParameters } from '@/plugins/api-plugin/services/api.service'
import {
    AddTrackingBody,
    CappedCountDao,
    ChangeOrderLineSkuBody,
    EditAddressesBody,
    OrderArchiveResultDao,
    OrderLineVersionBody,
    OrderEmailComposeDao,
    OrderEmailRenderedDao,
    OrderEmailSentDao,
    OrderEmailTemplatesDao,
    OrderEmailType,
    OrderLineWriteResultDao,
    OrderRowCreatedDao,
    OrderShipFormDao,
    OrderShipResultDao,
    SendOrderEmailBody,
    ShipOrderBody,
    OrderStatusWriteResultDao,
    SaveOrderLineBody,
    SkuCandidatesDao,
    SkuCandidatesQuery,
    SalesOrderAddressesForEditDao,
    SalesOrderRevealDao,
    SalesOrderQuickViewDao,
    SalesOrdersCountsDao,
    SalesOrdersExportBody,
    SalesOrdersIdsDao,
    SalesOrdersListBody,
    SalesOrdersLookupKind,
    SalesOrdersLookupName,
    SalesOrdersLookupPage,
    SalesOrdersPageDao,
    SalesOrdersQueryBody,
    SalesOrdersRefsCheckDao,
    SalesOrdersViewBody,
    SalesOrdersViewDao,
    SalesOrderActivityDao,
    SalesOrderCustomerDao,
    SalesOrderLinesDao,
    SalesOrderReturnsDao,
    SalesOrderShipmentsDao,
    SalesOrderSummaryDao,
    SalesOrderTimelineDao,
} from '@/interfaces/order/SalesOrders.dao'

const BASE = '/orders/sales-orders'

const ORDER_PARTS = ['summary', 'lines', 'activity', 'shipments', 'returns', 'customer', 'timeline'] as const
type OrderPart = (typeof ORDER_PARTS)[number]

/** The status writes of the order page, by their route. */
export type OrderStatusWrite = 'release-export' | 'release-review' | 'mark-paid'

/** The order archives: with a Cancellation PSOQ, or without one. */
export type OrderArchiveWrite = 'archive-with-psoq' | 'archive'

/**
 * The Sales Orders page (`/orders/sales-orders`, role `Web_Nav_Order_SalesOrder`). Reads send the
 * page state in the body, so a pasted list of 1,000 references travels safely. Every call reports
 * its own errors: the page shows them in place.
 */
export default class SalesOrdersService extends ApiService {
    /** POST list - one page of 50 by key; a newer call cancels the one in flight. */
    async list(body: SalesOrdersListBody): Promise<SalesOrdersPageDao> {
        this.cancelRequest(['salesOrdersList'])
        return this.request<SalesOrdersListBody, SalesOrdersPageDao>(
            'POST',
            `${BASE}/list`,
            undefined,
            body,
            undefined,
            'salesOrdersList',
            { suppressErrorAlert: true },
        )
    }

    /** POST counts - the queue tabs, the quick filters of the queue and Incomplete Archived. */
    async counts(body: SalesOrdersQueryBody): Promise<SalesOrdersCountsDao> {
        this.cancelRequest(['salesOrdersCounts'])
        return this.request<SalesOrdersQueryBody, SalesOrdersCountsDao>(
            'POST',
            `${BASE}/counts`,
            undefined,
            body,
            undefined,
            'salesOrdersCounts',
            { suppressErrorAlert: true },
        )
    }

    /** POST ids - every matching order id, up to 50,000. */
    async ids(body: SalesOrdersQueryBody): Promise<SalesOrdersIdsDao> {
        return this.request<SalesOrdersQueryBody, SalesOrdersIdsDao>(
            'POST',
            `${BASE}/ids`,
            undefined,
            body,
            undefined,
            'salesOrdersIds',
            { suppressErrorAlert: true },
        )
    }

    /** POST refs/check - how many orders a pasted list finds, and the references that find none. */
    async checkRefs(refs: string[]): Promise<SalesOrdersRefsCheckDao> {
        this.cancelRequest(['salesOrdersRefsCheck'])
        return this.request<{ refs: string[] }, SalesOrdersRefsCheckDao>(
            'POST',
            `${BASE}/refs/check`,
            undefined,
            { refs },
            undefined,
            'salesOrdersRefsCheck',
            { suppressErrorAlert: true },
        )
    }

    /** POST export/count - the export pre-count, exact up to 50,000. */
    async exportCount(body: SalesOrdersExportBody): Promise<CappedCountDao> {
        this.cancelRequest(['salesOrdersExportCount'])
        return this.request<SalesOrdersExportBody, CappedCountDao>(
            'POST',
            `${BASE}/export/count`,
            undefined,
            body,
            undefined,
            'salesOrdersExportCount',
            { suppressErrorAlert: true },
        )
    }

    /** POST export - the file; errors arrive as a Blob. */
    async export(body: SalesOrdersExportBody): Promise<AxiosResponse<Blob>> {
        return this.request<SalesOrdersExportBody, AxiosResponse<Blob>>(
            'POST',
            `${BASE}/export`,
            undefined,
            body,
            'blob',
            'salesOrdersExport',
            { suppressErrorAlert: true },
        )
    }

    /** GET lookups/{kind} - one page of a filter option list, scoped to the user. */
    async lookup(
        kind: SalesOrdersLookupKind,
        query: { search?: string; channels?: string[]; page?: number; limit?: number },
    ): Promise<SalesOrdersLookupPage> {
        return this.request<undefined, SalesOrdersLookupPage>(
            'GET',
            `${BASE}/lookups/${kind}`,
            { ...query },
            undefined,
            undefined,
            `salesOrdersLookup-${kind}`,
            { suppressErrorAlert: true },
        )
    }

    /** GET lookups/{kind}/names - the names behind the ids of a previous-page link or saved filter. */
    async lookupNames(kind: SalesOrdersLookupKind, ids: number[]): Promise<SalesOrdersLookupName[]> {
        return this.request<undefined, SalesOrdersLookupName[]>(
            'GET',
            `${BASE}/lookups/${kind}/names`,
            { ids },
            undefined,
            undefined,
            `salesOrdersLookupNames-${kind}`,
            { suppressErrorAlert: true },
        )
    }

    /** GET views - the Team views and the user's own. */
    async getViews(): Promise<SalesOrdersViewDao[]> {
        return this.request<undefined, SalesOrdersViewDao[]>(
            'GET',
            `${BASE}/views`,
            undefined,
            undefined,
            undefined,
            'salesOrdersViews',
            { suppressErrorAlert: true },
        )
    }

    /** POST views - saves the page state as a view; a taken name is a 409. */
    async createView(body: Required<SalesOrdersViewBody>): Promise<SalesOrdersViewDao> {
        return this.request<SalesOrdersViewBody, SalesOrdersViewDao>(
            'POST',
            `${BASE}/views`,
            undefined,
            body,
            undefined,
            'salesOrdersCreateView',
            { suppressErrorAlert: true },
        )
    }

    /** PATCH views/{id} - renames or overwrites a view the user saved. */
    async updateView(id: number, body: SalesOrdersViewBody): Promise<SalesOrdersViewDao> {
        return this.request<SalesOrdersViewBody, SalesOrdersViewDao>(
            'PATCH',
            `${BASE}/views/${id}`,
            undefined,
            body,
            undefined,
            'salesOrdersUpdateView',
            { suppressErrorAlert: true },
        )
    }

    /** DELETE views/{id} - deletes a view the user saved. */
    async deleteView(id: number): Promise<void> {
        await this.request<undefined, null>(
            'DELETE',
            `${BASE}/views/${id}`,
            undefined,
            undefined,
            undefined,
            'salesOrdersDeleteView',
            { suppressErrorAlert: true },
        )
    }

    /** GET {id}/quick-view - one order with its lines and latest notes. */
    async quickView(id: number): Promise<SalesOrderQuickViewDao> {
        this.cancelRequest(['salesOrdersQuickView'])
        return this.request<undefined, SalesOrderQuickViewDao>(
            'GET',
            `${BASE}/${id}/quick-view`,
            undefined,
            undefined,
            undefined,
            'salesOrdersQuickView',
            { suppressErrorAlert: true },
        )
    }

    /** GET {id}/summary - the order page header: row, gates, counts, progress, problems and the side cards. */
    async orderSummary(id: number): Promise<SalesOrderSummaryDao> {
        return this.orderPart<SalesOrderSummaryDao>(id, 'summary')
    }

    /** GET {id}/lines - the lines with their money columns and the totals. */
    async orderLines(id: number): Promise<SalesOrderLinesDao> {
        return this.orderPart<SalesOrderLinesDao>(id, 'lines')
    }

    /** GET {id}/activity - the notes, newest first, with their kind and the status of the time. */
    async orderActivity(id: number): Promise<SalesOrderActivityDao> {
        return this.orderPart<SalesOrderActivityDao>(id, 'activity')
    }

    /** GET {id}/shipments - shipments with tracking, supplier channel and Magento messages. */
    async orderShipments(id: number): Promise<SalesOrderShipmentsDao> {
        return this.orderPart<SalesOrderShipmentsDao>(id, 'shipments')
    }

    /** GET {id}/returns - PSOQs, credit notes and invoices; a list is null without its section role. */
    async orderReturns(id: number): Promise<SalesOrderReturnsDao> {
        return this.orderPart<SalesOrderReturnsDao>(id, 'returns')
    }

    /** GET {id}/timeline - the Where is it track: the order steps and one lane per PSOQ. */
    async orderTimeline(id: number): Promise<SalesOrderTimelineDao> {
        return this.orderPart<SalesOrderTimelineDao>(id, 'timeline')
    }

    /** GET {id}/customer - the addresses, masked, or only the country once redacted. */
    async orderCustomer(id: number): Promise<SalesOrderCustomerDao> {
        return this.orderPart<SalesOrderCustomerDao>(id, 'customer')
    }

    /** POST {id}/notes - adds a note, stored as typed. */
    async addOrderNote(id: number, note: string): Promise<OrderRowCreatedDao> {
        return this.orderWrite<{ note: string }, OrderRowCreatedDao>(id, 'notes', { note })
    }

    /** POST {id}/{write} - a status write on the version the page shows; 409 when the order changed since. */
    async orderStatusWrite(id: number, write: OrderStatusWrite, version: string): Promise<OrderStatusWriteResultDao> {
        return this.orderWrite<{ version: string }, OrderStatusWriteResultDao>(id, write, { version })
    }

    /** POST {id}/{write} - archives the order; 409 when the order changed since. */
    async archiveOrder(id: number, write: OrderArchiveWrite, version: string): Promise<OrderArchiveResultDao> {
        return this.orderWrite<{ version: string }, OrderArchiveResultDao>(id, write, { version })
    }

    /** POST {id}/lines/{lineId}/archive - archives one line with a Cancellation PSOQ. */
    async archiveLine(id: number, lineId: number, body: OrderLineVersionBody): Promise<OrderArchiveResultDao> {
        return this.orderWrite<OrderLineVersionBody, OrderArchiveResultDao>(id, `lines/${lineId}/archive`, body)
    }

    /** POST {id}/lines/{lineId}/save - saves the changed fields of one line; a lower quantity archives the rest. */
    async saveLine(id: number, lineId: number, body: SaveOrderLineBody): Promise<OrderLineWriteResultDao> {
        return this.orderWrite<SaveOrderLineBody, OrderLineWriteResultDao>(id, `lines/${lineId}/save`, body)
    }

    /** POST {id}/lines/{lineId}/change-sku - replaces the SKU of one line and re-allocates its stock. */
    async changeSku(id: number, lineId: number, body: ChangeOrderLineSkuBody): Promise<OrderLineWriteResultDao> {
        return this.orderWrite<ChangeOrderLineSkuBody, OrderLineWriteResultDao>(id, `lines/${lineId}/change-sku`, body)
    }

    /** GET {id}/lines/{lineId}/sku-candidates - one page of 100 SKUs; a new search cancels the one in flight. */
    async skuCandidates(id: number, lineId: number, query: SkuCandidatesQuery): Promise<SkuCandidatesDao> {
        this.cancelRequest(['salesOrderSkuCandidates'])
        return this.orderGet<SkuCandidatesDao>(id, `lines/${lineId}/sku-candidates`, 'salesOrderSkuCandidates', {
            ...query,
        })
    }

    /** POST {id}/addresses/edit - both addresses in clear for the edit sheet; the read is logged as a reveal. */
    async orderAddressesForEdit(id: number): Promise<SalesOrderAddressesForEditDao> {
        return this.orderWrite<undefined, SalesOrderAddressesForEditDao>(id, 'addresses/edit', undefined)
    }

    /** PUT {id}/addresses - saves both addresses and re-points the line VAT; 409 when the order changed since. */
    async saveOrderAddresses(id: number, body: EditAddressesBody): Promise<void> {
        return this.orderWrite<EditAddressesBody, void>(id, 'addresses', body, 'PUT')
    }

    /** POST {id}/reveal - email, phone and M/Place User ID in clear; the reveal is logged. */
    async revealOrderContact(id: number): Promise<SalesOrderRevealDao> {
        return this.orderWrite<undefined, SalesOrderRevealDao>(id, 'reveal', undefined)
    }

    /** GET {id}/email/templates - the Template Name list of a type for this order, by the CO rules. */
    async orderEmailTemplates(id: number, type: OrderEmailType): Promise<OrderEmailTemplatesDao> {
        return this.orderGet<OrderEmailTemplatesDao>(id, 'email/templates', 'salesOrderEmailTemplates', { type })
    }

    /** GET {id}/email/templates/{templateId} - the template subject and body rendered for this order. */
    async orderEmailTemplate(id: number, templateId: number): Promise<OrderEmailRenderedDao> {
        return this.orderGet<OrderEmailRenderedDao>(id, `email/templates/${templateId}`, 'salesOrderEmailTemplate')
    }

    /** POST {id}/email/compose - the From and To the sheet starts with; the read is logged as a reveal. */
    async orderEmailCompose(id: number): Promise<OrderEmailComposeDao> {
        return this.orderWrite<undefined, OrderEmailComposeDao>(id, 'email/compose', undefined)
    }

    /** POST {id}/email - sends the customer email as edited and adds the CO note. */
    async sendOrderEmail(id: number, body: SendOrderEmailBody): Promise<OrderEmailSentDao> {
        return this.orderWrite<SendOrderEmailBody, OrderEmailSentDao>(id, 'email', body)
    }

    /** GET {id}/ship - the rows of Ship order or Add tracking, the couriers and the order version. */
    async orderShipForm(id: number): Promise<OrderShipFormDao> {
        return this.orderGet<OrderShipFormDao>(id, 'ship', 'salesOrderShipForm')
    }

    /** POST {id}/ship - ships the rows by hand in one transaction, as the CO ShipOrder. */
    async shipOrder(id: number, body: ShipOrderBody): Promise<OrderShipResultDao> {
        return this.orderWrite<ShipOrderBody, OrderShipResultDao>(id, 'ship', body)
    }

    /** POST {id}/tracking - adds the missing tracking numbers of a Shipped Pending Tracking order. */
    async addOrderTracking(id: number, body: AddTrackingBody): Promise<OrderShipResultDao> {
        return this.orderWrite<AddTrackingBody, OrderShipResultDao>(id, 'tracking', body)
    }

    /** Cancels every order page read in flight, so a switch of order never shows the previous one. */
    cancelOrder() {
        this.cancelRequest(ORDER_PARTS.map((part) => `salesOrder-${part}`))
    }

    /** Cancels the ids read in flight, when the page that walks the list closes. */
    cancelIds() {
        this.cancelRequest(['salesOrdersIds'])
    }

    /** GET /orders/{id}/documents/confirmation - the order confirmation PDF; a failure throws with the server text. */
    async downloadConfirmation(id: number): Promise<AxiosResponse<Blob>> {
        return this.request<undefined, AxiosResponse<Blob>>(
            'GET',
            `/orders/${id}/documents/confirmation`,
            undefined,
            undefined,
            'blob',
            'salesOrderConfirmation',
            { suppressErrorAlert: true },
        )
    }

    /** GET /orders/{id}/documents/invoice/{number} - the invoice PDF; a failure throws with the server text. */
    async downloadInvoice(id: number, invoiceNumber: string): Promise<AxiosResponse<Blob>> {
        return this.request<undefined, AxiosResponse<Blob>>(
            'GET',
            `/orders/${id}/documents/invoice/${encodeURIComponent(invoiceNumber)}`,
            undefined,
            undefined,
            'blob',
            'salesOrderInvoice',
            { suppressErrorAlert: true },
        )
    }

    private async orderWrite<B, T>(id: number, path: string, body: B, method: Method = 'POST'): Promise<T> {
        return this.request<B, T>(
            method,
            `${BASE}/${id}/${path}`,
            undefined,
            body,
            undefined,
            `salesOrderWrite-${path}`,
            { suppressErrorAlert: true },
        )
    }

    private async orderGet<T>(id: number, path: string, callerName: string, params?: IRequestParameters): Promise<T> {
        return this.request<undefined, T>('GET', `${BASE}/${id}/${path}`, params, undefined, undefined, callerName, {
            suppressErrorAlert: true,
        })
    }

    private async orderPart<T>(id: number, part: OrderPart): Promise<T> {
        return this.orderGet<T>(id, part, `salesOrder-${part}`)
    }
}
